import React, {useEffect, useState, useRef} from 'react'
import PropTypes from 'prop-types'
import {scroller} from 'react-scroll'
import useMediaQuery from 'hec/hooks/useMediaQuery'
// Components
import {Accordion, AccordionItem, AccordionTrigger} from '@/components/ui/accordion/'
import {DelayedAccordionContent} from '@/components/accordion'
import {Button} from '@/components/ui/button'
import {Badge} from '@/components/ui/badge'
import QuizForm from './quizForm'
import PassedQuizBlock from './passedQuizBlock'
import FailedQuizBlock from './failedQuizBlock'
import CtaSection from './ctaSection'
import {Lock} from 'hec/assets/icons'
import LessonContentPreview from './lessonContentPreview'
import {getVisitedLessons, trackLessonVisit, isVisited} from 'hec/utils/guest-lesson-tracker'
import {useToast} from '@/hooks/useToast'
import {useIntl} from 'react-intl'

// Helpers & Constants
import {cn} from '@/lib/utils'
import {quizStatuses} from './lcModuleConstants'

// Builder dependencies
import {Content} from '@builder.io/sdk-react'
import builderConfig from 'hec/builder/model-map'

// Other
import {getConfig} from '@salesforce/pwa-kit-runtime/utils/ssr-config'
import {customComponents} from 'hec/builder'
import {useHistory} from 'react-router-dom'

const ModuleAccordion = ({moduleData}) => {
    const {
        module,
        requestInProgress,
        submitLesson,
        activeAccordionItem,
        setSelectedAccordionItem,
        quizStatus,
        setQuizStatus,
        submitQuiz,
        goToNextModule,
        nextModuleData,
        ctaType,
        isGuest,
        isLevelComplete,
        setActiveLesson
    } = moduleData

    const autoSubmittedIdsRef = useRef(new Set())
    const config = getConfig()
    const visitedLessons = getVisitedLessons()
    if (isGuest && activeAccordionItem) {
        trackLessonVisit(activeAccordionItem)
    }
    const isMobile = useMediaQuery('sm')
    const animationDelay = 300
    const history = useHistory()
    const showToast = useToast()
    const {formatMessage} = useIntl()

    const handleScroll = (el) => {
        setTimeout(() => {
            history.push(`#${el}`)
            scroller.scrollTo(el, {
                duration: 750,
                smooth: true,
                offset: isMobile ? -120 : -77
            })
        }, animationDelay)
    }

    useEffect(() => {
        const onHashChange = () => {
            const hash = window.location.hash.replace('#', '')
            if (hash) {
                setSelectedAccordionItem(hash)
                handleScroll(hash)
            }
        }
        window.addEventListener('hashchange', onHashChange)
        onHashChange()
        return () => window.removeEventListener('hashchange', onHashChange)
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [])

    useEffect(() => {
        handleScroll(activeAccordionItem)
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [activeAccordionItem])

    const didInitRef = useRef(false)

    useEffect(() => {
        if (didInitRef.current) return
        didInitRef.current = true

        // helper to run once the module + first open id exist
        const untilReadyThen = (fn, tries = 0) => {
            const openedId = activeAccordionItem
            const lessons = module?.lessons
            if (openedId && lessons?.length) {
                fn(openedId, lessons)
                return
            }
            if (tries > 60) return // ~3s max, safety
            setTimeout(() => untilReadyThen(fn, tries + 1), 50)
        }

        untilReadyThen((openedId, lessons) => {
            // 1) Pin the first-open accordion item so later recomputes won't advance it
            setSelectedAccordionItem(openedId)

            // 2) Optionally auto-mark complete for registered users, without advancing
            if (isGuest) return
            const openedLesson = lessons.find((l) => l.id === openedId)
            if (!openedLesson || openedLesson.complete) return

            // prevent double submit later
            autoSubmittedIdsRef.current.add(openedLesson.id)

            // IMPORTANT: mark only; DO NOT scroll/push hash/setActiveLesson here
            submitLesson(openedLesson, module.lessons?.[1], {markOnly: true})
        })
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [])

    const trackLesson = (lesson) => {
        if (isGuest && visitedLessons.length < 2) {
            trackLessonVisit(lesson.id)
            setActiveLesson(window.location.pathname + window.location.hash)
        } else if (isGuest) {
            showToast({
                title: formatMessage({id: 'You have reached the lesson limit for guest users'}),
                status: 'info',
                position: 'bottom'
            })
        }
    }

    const renderLessons = () => {
        const numOfLessons = module.lessons?.length
        return module.lessons?.map((lesson, i, lessons) => {
            return (
                <AccordionItem
                    value={lesson.id}
                    key={lesson.id}
                    disabled={lesson.disabled}
                    className={cn(
                        'border-b-0 [&[data-state=open]]:border-none',
                        i > 0 && 'border-t'
                    )}
                    id={lesson.id}
                    onClick={() => handleScroll(lesson.id)}
                    data-testid="moduleAccordion-AccordionItem"
                    name={lesson.id}
                >
                    <AccordionTrigger
                        icon={lesson.disabled ? <Lock /> : null}
                        className={cn(
                            'text-accent body-header-lg text-left pt-[1.1875rem] pb-[0.8125rem] pl-2 pr-3 lg:px-6 [&[data-state=open]]:bg-background/30 theme-medium',
                            lesson.disabled &&
                                '[&[data-state=closed]>svg]:rotate-0 [&[data-state=open]>svg]:-rotate-0 hover:no-underline',
                            !lesson.disabled &&
                                '[&[data-state=closed]>svg]:rotate-90 [&[data-state=open]>svg]:-rotate-90'
                        )}
                        id={lesson.id}
                        onClick={() => {
                            trackLesson(lesson)
                            // Logged-in: mark on expand (non-advancing), once per lesson
                            if (
                                !isGuest &&
                                !autoSubmittedIdsRef.current.has(lesson.id) &&
                                lesson.complete === false
                            ) {
                                autoSubmittedIdsRef.current.add(lesson.id)
                                submitLesson(lesson, null, {markOnly: true})
                            }
                        }}
                        data-testid="moduleAccordion-AccordionTrigger"
                    >
                        <div className="flex flex-1 justify-between mr-2">
                            {lesson.title || ''}
                            {isGuest ? (
                                <Badge variant={isVisited(lesson.id) ? 'default' : 'outline'}>
                                    {isVisited(lesson.id) ? 'Completed' : 'Get Started'}
                                </Badge>
                            ) : (
                                ''
                            )}
                        </div>
                    </AccordionTrigger>
                    <DelayedAccordionContent
                        animationDelay={animationDelay}
                        isOpen={activeAccordionItem === lesson.id}
                        className="px-2 pt-4 pb-8 lg:px-6 lg:pt-8 flex flex-col"
                        data-testid={'moduleAccordion-DelayedAccordionContent'}
                    >
                        <LessonContentPreview
                            content={lesson.content}
                            config={config}
                            builderModel={builderConfig.learningCenterLessonContent}
                            showFull={!isGuest || (isGuest && !!isVisited(lesson.id))}
                            onReadMoreClick={() => trackLesson(lesson)}
                        />
                        {isGuest && !isVisited(lesson.id) ? (
                            <a
                                href="#login"
                                className="inline"
                                data-testid="moduleAccordion-LoginLink"
                            >
                                <Button
                                    ctaType="button"
                                    variant="primary"
                                    className="w-full lg:w-[14.1875rem] lg:ml-auto"
                                    onClick={() => {
                                        trackLesson(lesson)
                                    }}
                                    disabled={
                                        requestInProgress ||
                                        (isGuest && i === numOfLessons - 1) ||
                                        (!isGuest &&
                                            i === numOfLessons - 1 &&
                                            !module.shouldQuizButtonBeEnabled)
                                    }
                                    data-testid="moduleAccordion-Button"
                                >
                                    {'Sign In to View More'}
                                </Button>
                            </a>
                        ) : (
                            <Button
                                ctaType="button"
                                variant="primary"
                                className="w-full lg:w-[14.1875rem] lg:ml-auto"
                                onClick={() => {
                                    trackLesson(lessons[i + 1])
                                    submitLesson(lesson, lessons[i + 1])
                                }}
                                disabled={
                                    requestInProgress ||
                                    (!module.shouldQuizButtonBeEnabled && i === numOfLessons - 1)
                                }
                                data-testid="moduleAccordion-Button"
                            >
                                {numOfLessons - 1 === i
                                    ? 'Continue: Quiz'
                                    : `Continue: Lesson ${i + 2}`}
                            </Button>
                        )}
                    </DelayedAccordionContent>
                </AccordionItem>
            )
        })
    }

    const renderQuiz = () => {
        const quiz = module.quiz
        if (!quiz) return null

        const disabled = !module.allLessonsComplete
        let contentToRender = ''

        switch (quizStatus) {
            case quizStatuses.OPEN:
                contentToRender = (
                    <div className="px-2 lg:px-6">
                        <QuizForm
                            module={module}
                            requestInProgress={requestInProgress}
                            submitQuiz={(data) => {
                                submitQuiz(data)
                                handleScroll(quiz.id)
                            }}
                            data-testid="moduleAccordion-QuizForm"
                        />
                    </div>
                )
                break
            case quizStatuses.PASSED:
                contentToRender = (
                    <PassedQuizBlock
                        moduleTitle={module.title}
                        data-testid="moduleAccordion-PassedQuizBlock"
                    />
                )
                break
            case quizStatuses.FAILED:
                contentToRender = <FailedQuizBlock data-testid="moduleAccordion-FailedQuizBlock" />
                break
        }

        return (
            <AccordionItem
                value={quiz.id}
                key={quiz.id}
                disabled={disabled}
                className="border-b-0 [&[data-state=open]]:border-none border-t"
                name={quiz.id}
                data-testid="moduleAccordion-AccordionItem"
            >
                <AccordionTrigger
                    icon={disabled ? <Lock /> : null}
                    className={cn(
                        'theme-medium text-accent body-header-lg text-left pt-[1.1875rem] pb-[0.8125rem] pl-2 pr-3 lg:px-6 [&[data-state=open]]:bg-background/30',
                        disabled &&
                            '[&[data-state=closed]>svg]:rotate-0 [&[data-state=open]>svg]:-rotate-0 hover:no-underline',
                        !disabled &&
                            '[&[data-state=closed]>svg]:rotate-90 [&[data-state=open]>svg]:-rotate-90'
                    )}
                    onClick={() => handleScroll(quiz.id)}
                    data-testid="moduleAccordion-AccordionTrigger"
                >
                    Quiz
                </AccordionTrigger>
                <DelayedAccordionContent
                    isOpen={activeAccordionItem === quiz.id}
                    className="pt-4 pb-8 lg:pr-12 lg:pt-8 flex flex-col"
                    data-testid={'moduleAccordion-DelayedAccordionContent'}
                >
                    {module?.allLessonsComplete && (
                        <>
                            <div className="theme-medium bg-background/30 mb-11 lg:mb-[3.25rem] py-6 px-2 lg:px-6 rounded-md">
                                <Content
                                    model={builderConfig.learningCenterModuleSummary}
                                    content={module.summary?.value}
                                    apiKey={config.app.builder.api}
                                    enrich={true}
                                    customComponents={customComponents}
                                    data-testid="moduleAccordion-Content"
                                />
                            </div>
                            {contentToRender}
                        </>
                    )}
                </DelayedAccordionContent>
            </AccordionItem>
        )
    }

    return (
        <Accordion
            type="single"
            collapsible
            value={activeAccordionItem}
            onValueChange={setSelectedAccordionItem}
            data-testid="moduleAccordion"
        >
            {renderLessons()}
            {renderQuiz()}
            {(quizStatus === quizStatuses.FAILED || quizStatus === quizStatuses.PASSED) && (
                <CtaSection
                    quizStatus={quizStatus}
                    setQuizStatus={setQuizStatus}
                    requestInProgress={requestInProgress}
                    goToNextModule={goToNextModule}
                    nextModuleData={nextModuleData}
                    ctaType={ctaType}
                    isLevelComplete={isLevelComplete}
                    data-testid="moduleAccordion-CtaSection"
                />
            )}
        </Accordion>
    )
}

ModuleAccordion.propTypes = {
    moduleData: PropTypes.object.isRequired
}

export default ModuleAccordion
